import { PiChatCircleText } from 'react-icons/pi';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { WhatsAppWindowHelp } from './whatsapp-window-help';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { WhatsAppSessionComposerProps } from 'src/modules/communications/types/whatsapp/template-session-notice.types';

/**
 * Replaces the expired editor with a direct path to approved messages.
 * @param props - Current feature state and consumer-owned callbacks.
 * @returns The corresponding composer surface.
 */
export const WhatsAppSessionComposer = ({
  loading,
  connectionAvailable,
  connectionLoading,
  isTestConversation,
  sessionActive,
  fromAddress,
  toAddress,
  children,
  expired,
  waitingForReply,
  disabled,
  onChooseTemplate,
}: WhatsAppSessionComposerProps) => {
  const { t } = useI18n();
  if (loading && (!fromAddress || !toAddress)) {
    return (
      <div
        aria-hidden="true"
        className="flex h-11 items-center rounded-xl border border-secondary-200 px-3"
      >
        <TextSkeleton variant="neutral" className="max-w-48" />
      </div>
    );
  }
  if (
    loading ||
    (!connectionAvailable && !connectionLoading) ||
    isTestConversation ||
    sessionActive ||
    !fromAddress ||
    !toAddress
  ) {
    return children;
  }
  return (
    <div className="flex min-h-[2.75rem] flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-secondary-200 bg-secondary-50 px-3 py-1">
      <div className="flex min-w-0 flex-1 basis-40 items-center gap-2">
        <PiChatCircleText
          aria-hidden
          className="h-5 w-5 shrink-0 text-secondary-600"
        />
        <div className="min-w-0 flex-1" role="status">
          <Text size="tiny" color="secondary">
            {t(
              waitingForReply
                ? 'whatsappTemplates.noticeWaiting'
                : expired
                  ? 'whatsappTemplates.noticeExpired'
                  : 'whatsappTemplates.noticeNew'
            )}
          </Text>
        </div>
      </div>
      <div className="ml-auto flex shrink-0 items-center gap-2">
        <WhatsAppWindowHelp
          key={`${fromAddress}:${toAddress}`}
          expired={expired}
          waitingForReply={waitingForReply}
        />
        <Button
          variant="whatsapp"
          appearance="soft"
          size="xs"
          onClick={onChooseTemplate}
          disabled={disabled}
        >
          {t(
            waitingForReply
              ? 'whatsappTemplates.anotherAction'
              : 'whatsappTemplates.chooseAction'
          )}
        </Button>
      </div>
    </div>
  );
};
